import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { QuoteParagraph } from "@repo/app-types/helpers/gradeValidationHelpers";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  QuoteMatcher,
  QuoteMatchMap,
} from "../teammatePerformance/gradeValidationHelpers.js";
import { useSortedRecordings } from "../teammatePerformance/gradingHelpers.js";
import type { GradingTranscriptWithIndexHandle } from "../teammatePerformance/gradingTranscriptWithIndex.js";

interface UseQuoteToTranscriptArgs {
  recordings: readonly AccountGradingRecording[];
  quoteParagraphs: readonly QuoteParagraph[];
  /** When true, skip the initial auto-scroll to the first matched quote. */
  skipAutoScroll?: boolean;
}

/**
 * Manages the common pattern of matching quote paragraphs to transcript
 * fragments for highlighting, scrolling, and interactive quote presses.
 *
 * Returns refs that should be wired to the transcript container and
 * `GradingTranscriptWithIndex`, plus the derived `quoteMatches` and
 * a `handleQuotePress` callback.
 */
export function useMatchQuoteToTranscript({
  recordings,
  quoteParagraphs,
  skipAutoScroll,
}: UseQuoteToTranscriptArgs) {
  const sortedRecordings = useSortedRecordings(recordings);

  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<GradingTranscriptWithIndexHandle>(null);

  const quoteMatcher = useMemo(() => {
    return QuoteMatcher.new(sortedRecordings);
  }, [sortedRecordings]);

  const quoteMatches = useMemo(() => {
    return quoteMatcher.findMatches(quoteParagraphs);
  }, [quoteMatcher, quoteParagraphs]);

  const handleQuotePress = useCallback(
    (quoteId: string) => {
      const currentTranscriptContainer = transcriptContainerRef.current;
      const currentTranscript = transcriptRef.current;
      if (!currentTranscriptContainer || !currentTranscript) {
        return;
      }

      currentTranscript.focusQuote({
        container: currentTranscriptContainer,
        quoteId,
        highlightQuote: true,
      });
    },
    [transcriptContainerRef],
  );

  // Auto-scroll to the first matched quote on mount
  const firstQuoteId = useMemo(() => {
    const firstQuote = quoteParagraphs.find((q) =>
      QuoteMatchMap.findByQuote(quoteMatches, q.id),
    );
    return firstQuote?.id;
  }, [quoteParagraphs, quoteMatches]);

  useEffect(() => {
    if (skipAutoScroll) return;

    const currentTranscriptContainer = transcriptContainerRef.current;
    const currentTranscript = transcriptRef.current;
    if (!currentTranscriptContainer || !currentTranscript || !firstQuoteId) {
      return;
    }

    currentTranscript.focusQuote({
      container: currentTranscriptContainer,
      quoteId: firstQuoteId,
      highlightQuote: false,
    });
  }, [firstQuoteId, skipAutoScroll]);

  return {
    sortedRecordings,
    quoteMatches,
    handleQuotePress,
    transcriptRef,
    transcriptContainerRef,
  };
}
